import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, NgModule, SkipSelf } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { switchMap, map, catchError } from 'rxjs/operators';
import { of, zip } from 'rxjs';

/**
 * Custom HttpParameterCodec
 * Workaround for https://github.com/angular/angular/issues/18261
 */
class CustomHttpParameterCodec {
    encodeKey(k) {
        return encodeURIComponent(k);
    }
    encodeValue(v) {
        return encodeURIComponent(v);
    }
    decodeKey(k) {
        return decodeURIComponent(k);
    }
    decodeValue(v) {
        return decodeURIComponent(v);
    }
}

const BASE_PATH = new InjectionToken('basePath');
const COLLECTION_FORMATS = {
    csv: ',',
    tsv: '   ',
    ssv: ' ',
    pipes: '|',
};

class Configuration {
    constructor(configurationParameters = {}) {
        this.apiKeys = configurationParameters.apiKeys;
        this.username = configurationParameters.username;
        this.password = configurationParameters.password;
        this.accessToken = configurationParameters.accessToken;
        this.basePath = configurationParameters.basePath;
        this.withCredentials = configurationParameters.withCredentials;
        this.encoder = configurationParameters.encoder;
        if (configurationParameters.credentials) {
            this.credentials = configurationParameters.credentials;
        }
        else {
            this.credentials = {};
        }
    }
    /**
     * Select the correct content-type to use for a request.
     * Uses {@link Configuration#isJsonMime} to determine the correct content-type.
     * If no content type is found return the first found type if the contentTypes is not empty
     * @param contentTypes - the array of content types that are available for selection
     * @returns the selected content-type or <code>undefined</code> if no selection could be made.
     */
    selectHeaderContentType(contentTypes) {
        if (contentTypes.length === 0) {
            return undefined;
        }
        const type = contentTypes.find((x) => this.isJsonMime(x));
        if (type === undefined) {
            return contentTypes[0];
        }
        return type;
    }
    /**
     * Select the correct accept content-type to use for a request.
     * Uses {@link Configuration#isJsonMime} to determine the correct accept content-type.
     * If no content type is found return the first found type if the contentTypes is not empty
     * @param accepts - the array of content types that are available for selection.
     * @returns the selected content-type or <code>undefined</code> if no selection could be made.
     */
    selectHeaderAccept(accepts) {
        if (accepts.length === 0) {
            return undefined;
        }
        const type = accepts.find((x) => this.isJsonMime(x));
        if (type === undefined) {
            return accepts[0];
        }
        return type;
    }
    /**
     * Check if the given MIME is a JSON MIME.
     * JSON MIME examples:
     *   application/json
     *   application/json; charset=UTF8
     *   APPLICATION/JSON
     *   application/vnd.company+json
     * @param mime - MIME (Multipurpose Internet Mail Extensions)
     * @return True if the given MIME is JSON, false otherwise.
     */
    isJsonMime(mime) {
        const jsonMime = new RegExp('^(application/json|[^;/ \t]+/[^;/ \t]+[+]json)[ \t]*(;.*)?$', 'i');
        return (mime !== null &&
            (jsonMime.test(mime) ||
                mime.toLowerCase() === 'application/json-patch+json'));
    }
    lookupCredential(key) {
        const value = this.credentials[key];
        return typeof value === 'function' ? value() : value;
    }
}

/**
 * Screenshot API
 * Screenshot API allows you to query for screenshots of a device by device ID and current user\' session ID.
 *
 * The version of the OpenAPI document: 1.1.1
 * Contact: support@stratacache.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
class DeviceMetadataService {
    constructor(httpClient, basePath, configuration) {
        this.httpClient = httpClient;
        this.basePath = '';
        this.defaultHeaders = new HttpHeaders();
        this.configuration = new Configuration();
        if (configuration) {
            this.configuration = configuration;
        }
        if (typeof this.configuration.basePath !== 'string') {
            if (typeof basePath !== 'string') {
                basePath = this.basePath;
            }
            this.configuration.basePath = basePath;
        }
        this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    addToHttpParams(httpParams, value, key) {
        if (typeof value === 'object' && value instanceof Date === false) {
            httpParams = this.addToHttpParamsRecursive(httpParams, value);
        }
        else {
            httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
        }
        return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
        if (value == null) {
            return httpParams;
        }
        if (typeof value === 'object') {
            if (Array.isArray(value)) {
                value.forEach((elem) => (httpParams = this.addToHttpParamsRecursive(httpParams, elem, key)));
            }
            else if (value instanceof Date) {
                if (key != null) {
                    httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
                }
                else {
                    throw Error('key may not be null if value is Date');
                }
            }
            else {
                Object.keys(value).forEach((k) => (httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k)));
            }
        }
        else if (key != null) {
            httpParams = httpParams.append(key, value);
        }
        else {
            throw Error('key may not be null if value is not object or array');
        }
        return httpParams;
    }
    getDeviceMetadata(deviceId, sessionId, _with, observe = 'body', reportProgress = false, options) {
        if (deviceId === null || deviceId === undefined) {
            throw new Error('Required parameter deviceId was null or undefined when calling getDeviceMetadata.');
        }
        if (sessionId === null || sessionId === undefined) {
            throw new Error('Required parameter sessionId was null or undefined when calling getDeviceMetadata.');
        }
        let queryParameters = new HttpParams({ encoder: this.encoder });
        if (_with !== undefined && _with !== null) {
            queryParameters = this.addToHttpParams(queryParameters, _with, 'with');
        }
        let headers = this.defaultHeaders;
        let httpHeaderAcceptSelected = options && options.httpHeaderAccept;
        if (httpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts = [
                'application/xml',
                'application/json',
            ];
            httpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (httpHeaderAcceptSelected !== undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }
        let responseType = 'json';
        if (httpHeaderAcceptSelected &&
            httpHeaderAcceptSelected.startsWith('text')) {
            responseType = 'text';
        }
        return this.httpClient.get(`${this.configuration.basePath}/device/${encodeURIComponent(String(deviceId))}/${encodeURIComponent(String(sessionId))}/CM/screenshot.cgi`, {
            params: queryParameters,
            responseType: responseType,
            withCredentials: this.configuration.withCredentials,
            headers: headers,
            observe: observe,
            reportProgress: reportProgress,
        });
    }
}
DeviceMetadataService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.2", ngImport: i0, type: DeviceMetadataService, deps: [{ token: i1.HttpClient }, { token: BASE_PATH, optional: true }, { token: Configuration, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
DeviceMetadataService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.2", ngImport: i0, type: DeviceMetadataService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.2", ngImport: i0, type: DeviceMetadataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [BASE_PATH]
                }] }, { type: Configuration, decorators: [{
                    type: Optional
                }] }]; } });

class PlayerService {
    constructor(deviceMetadataService, basePath, configuration) {
        this.deviceMetadataService = deviceMetadataService;
        this.basePath = '';
        this.configuration = new Configuration();
        if (configuration) {
            this.configuration = configuration;
        }
        if (typeof this.configuration.basePath !== 'string') {
            if (typeof basePath !== 'string') {
                basePath = this.basePath;
            }
            this.configuration.basePath = basePath;
        }
        this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    /**
     * Get this device's all logical players output port imageUrls
     * @param deviceId Device ID
     */
    getAllPlayersOutputPortImageUrls(deviceId) {
        if (deviceId === null || deviceId === undefined) {
            throw new Error('Required parameter deviceId was null or undefined when calling getAllPlayersOutputPortImageUrls.');
        }
        return this._getDeviceMetadata(deviceId).pipe(switchMap((dto) => {
            const imgUrls = this._getAllScreenshotUrls(dto, deviceId);
            return of(imgUrls);
        }));
    }
    /**
     * Get a list of devices' all screenshots
     * @deprecated no added value for this call than getAllPlayersOutputPortImageUrls
     * @param deviceIds List of Device IDs
     */
    getDevicesAllScreenshots(deviceIds) {
        if (deviceIds === null || deviceIds === undefined) {
            throw new Error('Required parameter deviceIds was null or undefined when calling getDeviceMetadata.');
        }
        let obs$ = [];
        deviceIds.forEach((id) => {
            obs$.push(this.getAllPlayersOutputPortImageUrls(id).pipe(map((res) => {
                return { id: id, urls: res };
            })));
        });
        return zip(...obs$);
    }
    /**
     * Get a single device's all screenshots
     * @deprecated no added value for this call than getAllPlayersOutputPortImageUrls
     * @param deviceId Device ID
     */
    getDeviceAllScreenshots(deviceId) {
        if (deviceId === null || deviceId === undefined) {
            throw new Error('Required parameter deviceId was null or undefined when calling getDeviceMetadata.');
        }
        return this.getAllPlayersOutputPortImageUrls(deviceId).pipe(map((res) => {
            return { id: deviceId, urls: res };
        }));
    }
    /**
     * Find live display of a given device/player/port
     * @param deviceId
     * @param playerId
     * @param portIndex
     */
    getDeviceScreenshotByPlayerAndPort(deviceId, playerId, portIndex) {
        if (deviceId === null || deviceId === undefined) {
            throw new Error('Required parameter deviceId was null or undefined when calling getDeviceScreenshotByPlayerAndPort.');
        }
        if (playerId === null || playerId === undefined) {
            throw new Error('Required parameter playerId was null or undefined when calling getDeviceScreenshotByPlayerAndPort.');
        }
        if (portIndex === null || portIndex === undefined) {
            throw new Error('Required parameter portIndex was null or undefined when calling getDeviceScreenshotByPlayerAndPort.');
        }
        return this._getDeviceMetadata(deviceId).pipe(switchMap((dto) => {
            const targetPlayer = dto?.channels?.find((player) => player.id === playerId);
            const imgUrls = this._scanDisplays(deviceId, dto.subdir, targetPlayer);
            return of(imgUrls[portIndex]);
        }), catchError((err) => {
            throw err;
        }));
    }
    /**
     * Returns live info about the device and its logical players (including monitoring data)
     * @param deviceId
     */
    getLiveDeviceInfo(deviceId) {
        if (deviceId === null || deviceId === undefined) {
            throw new Error('Required parameter deviceId was null or undefined when calling getLiveDeviceInfo.');
        }
        return this._getDeviceMetadata(deviceId, true).pipe(switchMap((dto) => {
            const logicalPlayers = this._scanLogicalPlayers(deviceId, dto);
            return of({
                events: dto?.monitoring?.events,
                logicalPlayers,
                ...(dto?.monitoring?.sample_time ? { sampleTime: dto.monitoring.sample_time } : {})
            });
        }), catchError((err) => {
            throw err;
        }));
    }
    /**
     * Get metadata of a device
     * @param deviceId Device ID
     * @param withMonitoring wheather ask for monitoring data
     */
    _getDeviceMetadata(deviceId, withMonitoring) {
        if (deviceId === null || deviceId === undefined) {
            throw new Error('Required parameter deviceId was null or undefined when calling getDeviceMetadata.');
        }
        return (withMonitoring
            ? this.deviceMetadataService.getDeviceMetadata(deviceId, this.configuration.apiKeys['sessionId'], 'monitoring')
            : this.deviceMetadataService.getDeviceMetadata(deviceId, this.configuration.apiKeys['sessionId'])).pipe(switchMap((dto) => {
            if (!!dto) {
                // has to handle two different type of results: due to two possible return of payloads
                const deviceMetadata = dto.hasOwnProperty('result')
                    ? dto.result
                    : { ...dto };
                return of(deviceMetadata);
            }
            else {
                return of({});
            }
        }), catchError((err) => {
            throw err;
        }));
    }
    /**
     * extract screenshot urls from ScreenshotMetadataDTO
     * @param screenshotMetadataDTO
     * @param deviceId
     */
    _getAllScreenshotUrls(screenshotMetadataDTO, deviceId) {
        let urls = [];
        if (screenshotMetadataDTO.hasOwnProperty('channels')) {
            screenshotMetadataDTO.channels.forEach((lplayer) => {
                const displays = this._scanDisplays(deviceId, screenshotMetadataDTO.subdir, lplayer);
                urls.push(...displays.map((display) => display.screen));
            });
        }
        return urls;
    }
    _scanDisplays(deviceId, subDir, player) {
        let displays = [];
        if (player && player.hasOwnProperty('shots')) {
            player.shots.forEach((port) => {
                const screenshot = `${this.configuration.basePath}/device/${encodeURIComponent(String(deviceId))}/${encodeURIComponent(String(this.configuration.apiKeys['sessionId']))}/tmp/CM/${encodeURIComponent(String(subDir))}/${encodeURIComponent(String(port.screenshot))}`;
                const desktop = `${this.configuration.basePath}/device/${encodeURIComponent(String(deviceId))}/${encodeURIComponent(String(this.configuration.apiKeys['sessionId']))}/tmp/CM/${encodeURIComponent(String(subDir))}/${encodeURIComponent(String(port.desktop))}`;
                const desktopFull = `${this.configuration.basePath}/device/${encodeURIComponent(String(deviceId))}/${encodeURIComponent(String(this.configuration.apiKeys['sessionId']))}/tmp/CM/${encodeURIComponent(String(subDir))}/${encodeURIComponent(String(port.desktop_full))}`;
                let ratio = undefined;
                if (port.hasOwnProperty('geometry')) {
                    try {
                        ratio =
                            Number(port.geometry.split('x')[0]) /
                                Number(port.geometry.split('x')[1]);
                    }
                    catch {
                        ratio = undefined;
                    }
                }
                displays.push({
                    screen: {
                        screenshot: port.screenshot ? screenshot : undefined,
                        desktop: port.desktop ? desktop : undefined,
                        desktopFull: port.desktop_full ? desktopFull : undefined,
                        ratio: ratio,
                    },
                    displayStatus: port.display?.displays,
                    on: port.display?.on,
                    off: port.display?.off,
                    show: port.display?.show,
                    total: port.display?.total,
                });
            });
        }
        return displays;
    }
    _scanLogicalPlayers(deviceId, meta) {
        let logicalPlayers = [];
        // get monitoring
        let monitoringData = {};
        if (meta && meta.hasOwnProperty('monitoring')) {
            meta.monitoring?.modules.forEach((module) => {
                monitoringData[module.id] = this._scanPlayerMonitoringData(module);
            });
        }
        if (meta && meta.hasOwnProperty('channels')) {
            meta.channels.forEach((channel) => {
                const displays = this._scanDisplays(deviceId, meta.subdir, channel);
                logicalPlayers.push(meta.hasOwnProperty('monitoring')
                    ? {
                        id: channel.id,
                        show: channel.show,
                        title: channel.title,
                        error: channel.error,
                        outputs: displays,
                        monitoring: monitoringData[channel.id],
                    }
                    : {
                        id: channel.id,
                        show: channel.show,
                        title: channel.title,
                        error: channel.error,
                        outputs: displays,
                    });
            });
        }
        return logicalPlayers;
    }
    _scanPlayerMonitoringData(module) {
        return {
            id: module.id,
            state: module.state,
            state_msg: module.state_msg,
            playlistTitle: module?.playlist?.title,
            playlistSubtitle: module?.playlist?.subtitles,
            events: module?.events?.map((event) => ({
                id: event.id,
                name: event.name,
                ns: event.ns,
                level: event.level,
                levelstr: event.levelstr,
                msg: event.msg,
                longmsg: event.longmsg,
                data: event.data,
                firsttime: event.firsttime,
                endtime: event.endtime,
            })),
        };
    }
}
PlayerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.2", ngImport: i0, type: PlayerService, deps: [{ token: DeviceMetadataService }, { token: BASE_PATH, optional: true }, { token: Configuration, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
PlayerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.2", ngImport: i0, type: PlayerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.2", ngImport: i0, type: PlayerService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: DeviceMetadataService }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [BASE_PATH]
                }] }, { type: Configuration, decorators: [{
                    type: Optional
                }] }]; } });

const APIS = [DeviceMetadataService, PlayerService];

/**
 * Screenshot API
 * Screenshot API allows you to query for screenshots of a device by device ID and current user\' session ID.
 *
 * The version of the OpenAPI document: 1.1.1
 * Contact: support@stratacache.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Screenshot API
 * Screenshot API allows you to query for screenshots of a device by device ID and current user\' session ID.
 *
 * The version of the OpenAPI document: 1.1.1
 * Contact: support@stratacache.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Screenshot API
 * Screenshot API allows you to query for screenshots of a device by device ID and current user\' session ID.
 *
 * The version of the OpenAPI document: 1.1.1
 * Contact: support@stratacache.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Physical screens that attached to each player's out put port
 */

class PlayerAPIModule {
    constructor(parentModule, http) {
        if (parentModule) {
            throw new Error('ApiModule is already loaded. Import in your base AppModule only.');
        }
        if (!http) {
            throw new Error('You need to import the HttpClientModule in your AppModule! \n' +
                'See also https://github.com/angular/angular/issues/20575');
        }
    }
    static forRoot(configurationFactory) {
        return {
            ngModule: PlayerAPIModule,
            providers: [{ provide: Configuration, useFactory: configurationFactory }],
        };
    }
}
PlayerAPIModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.2", ngImport: i0, type: PlayerAPIModule, deps: [{ token: PlayerAPIModule, optional: true, skipSelf: true }, { token: i1.HttpClient, optional: true }], target: i0.ɵɵFactoryTarget.NgModule });
PlayerAPIModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.2", ngImport: i0, type: PlayerAPIModule });
PlayerAPIModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.2", ngImport: i0, type: PlayerAPIModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.2", ngImport: i0, type: PlayerAPIModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    declarations: [],
                    exports: [],
                    providers: [],
                }]
        }], ctorParameters: function () { return [{ type: PlayerAPIModule, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }, { type: i1.HttpClient, decorators: [{
                    type: Optional
                }] }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { APIS, BASE_PATH, COLLECTION_FORMATS, Configuration, DeviceMetadataService, PlayerAPIModule, PlayerService };
